<template>
  <form
    class="form"
    method="post"
    novalidate
    @submit="onSubmit"
  >
    <fn-loading-overlay v-show="loading" />
    <slot v-bind="form" />
  </form>
</template>

<script setup lang="ts">
import type { FormProps, FormEmits } from '@/types/forms';
import type { SubmissionHandler, InvalidSubmissionHandler } from 'vee-validate';

const props = defineProps<FormProps>();
const emit = defineEmits<FormEmits>();
const model = defineModel<any>();

/**
 * Create vee-validate form
 */
const form = useForm({
  validateOnMount: false,
  initialValues: model.value,
  initialErrors: props.errors,
});

model.value = form.values;

const resetErrors = () => {
  const errors = unref(form.errors);

  for (const key of Object.keys(errors)) {
    errors[key] = '';
  }

  form.setErrors(errors);
  emit('errorsCleared');
};

resetErrors();

watch(
  () => props.errors,
  (newVal) => {
    if (!newVal) {
      form.resetForm();
      return;
    }

    form.setErrors(newVal);
  },
);

watch(
  () => unref(form?.isValidating),
  (isValidating) => {
    if (isValidating || !props.scrollToFieldOnValidationError) {
      return;
    }

    const validationErrors = unref(unref(form)?.errorBag);
    if (!validationErrors || !Object.keys(validationErrors)?.length) {
      return;
    }
    const firstInvalidFormEl = document.querySelector(`input[name=${Object.keys(validationErrors)[0]}]`);
    const elToScroll = firstInvalidFormEl || document.querySelector('.form');

    elToScroll?.scrollIntoView({
      behavior: 'smooth',
    });
  },
);

const onSubmitValid: SubmissionHandler = (values, ctx) => {
  if (props.disabled) {
    return;
  }

  emit('submit', { values, ctx });
};

const onSubmitInvalid: InvalidSubmissionHandler = (ctx) => {
  emit('invalidSubmit', ctx);
};

const onSubmit = form.handleSubmit(onSubmitValid, onSubmitInvalid);

onMounted(() => {
  emit('formLoaded', {
    form,
    resetErrors,
  });
});

defineExpose({
  form,
  resetErrors,
});
</script>

<style lang="scss" scoped>
.form {
  position: relative;

  :deep(.grid-container) {
    padding-top: 5px;
  }

  :deep(.grid-container.birthday) {
    padding-top: 0;
  }
}
</style>
